import { useMemo, useState, useEffect, useRef } from 'react';
import { useAtomValue } from 'jotai';
import { useQueryClient } from '@tanstack/react-query';
import Fuse from 'fuse.js';
import { useStateReducer, showNotification, captureException } from 'r/utils';
import { hlFilteredPairsAtom } from 'r/atoms';
import {
  DELETE_ANALYSES_CMS,
  DELETE_ANALYSES,
  DELETE_MARKET_SCORE,
  fetchApiRaw,
  PUT_ANALYSES,
  PUT_ANALYSES_CMS,
  PUT_MARKET_SCORE,
} from 'r/utils/fetchApiRaw';
import {
  isMarketFrozen,
  isMarketStarted,
  titleForSymbol,
  type ScoreMarketBody,
} from 'r/utils/scoreMarket';
import { notifyScoreError } from '../../ScoreMarketsTab/mutations';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import SpinnerCircle from 'r/ui/SpinnerCircle';
import { ChevronDown, Check } from 'lucide-react';
import type { CryptoPair, Analyse, ScoreMarket } from 'r/types';
import { StoredAuth } from 'r/utils/Stored';
import { cn } from '@/lib/utils';
import { MEME_URLS, GOOD_MEMES, BAD_MEMES } from 'r/constants/memes';

export default function ModalAdminEditAnalysis({
  analysis,
  sanitizerAnalysis,
  existingMarket,
  onClose,
  onAnalysisUpdated,
  onDelete,
}: {
  analysis: Analyse;
  sanitizerAnalysis: Analyse | undefined;
  existingMarket?: ScoreMarket;
  onClose: () => void;
  onAnalysisUpdated: () => void;
  onDelete?: () => void;
}) {
  const [isOpen, setIsOpen] = useState(true);
  const [isLoadingModalShown, setIsLoadingModalShown] = useState(false);
  const [pairSearchInput, setPairSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [comboboxOpen, setComboboxOpen] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const queryClient = useQueryClient();
  const pairs = useAtomValue(hlFilteredPairsAtom);
  const accessTokens = StoredAuth.getAll();

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(pairSearchInput);
    }, 200);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [pairSearchInput]);

  const isMarketLive = !!existingMarket && isMarketStarted(existingMarket);
  const isMarketLocked = !!existingMarket && isMarketFrozen(existingMarket);

  // Analysis edits follow the Score rule: KOL, pair and direction only
  // reach the market while nobody has predicted on it
  const syncToMarket = async (body: ScoreMarketBody, label: string) => {
    if (!existingMarket) {
      showNotification('success', `${label} updated successfully`);
    } else if (isMarketLocked) {
      showNotification(
        'success',
        `${label} updated in analysis (Score market has predictions and was not changed)`,
      );
    } else {
      try {
        await fetchApiRaw(
          PUT_MARKET_SCORE,
          { urlParams: [existingMarket.id], body },
          accessTokens.cms,
        );
        queryClient.invalidateQueries({ queryKey: ['score-markets'] });
        showNotification('success', `${label} updated in analysis and Score`);
      } catch (e) {
        notifyScoreError(e, `${label} updated in analysis, Score sync failed`);
      }
    }
  };

  const pairsPrepared = useMemo(
    () =>
      pairs.map((pair: CryptoPair) => ({
        ...pair,
        label: pair.symbol,
        icon: pair.logoUrl,
      })),
    [pairs],
  );

  const fuseInstance = useMemo(() => {
    return new Fuse(pairsPrepared, { keys: ['label'], threshold: 0.3 });
  }, [pairsPrepared]);

  const filteredPairs = useMemo(() => {
    if (debouncedSearch === '') {
      return pairsPrepared;
    }
    return fuseInstance.search(debouncedSearch).map(({ item }) => item);
  }, [debouncedSearch, pairsPrepared, fuseInstance]);

  const [{ pair, isDeleting, memeFileName, isMemeSaving }, ss] =
    useStateReducer({
      pair: pairsPrepared.find((p: CryptoPair) => p.symbol === analysis.pair),
      isDeleting: false,
      memeFileName: (sanitizerAnalysis?.memeURL ?? analysis.memeURL) || null,
      isMemeSaving: false,
    });

  // Same rule as the Sanitizer: positive performance allows only "good"
  // memes, negative only "bad". Without performance both packs are allowed.
  const performance = analysis.performance ?? sanitizerAnalysis?.performance;
  const hasPerformance =
    typeof performance === 'number' && Number.isFinite(performance);
  const isMemeRowActive = (isGoodRow: boolean) =>
    !hasPerformance || (isGoodRow ? performance >= 0 : performance < 0);

  const handleClose = () => {
    setIsOpen(false);
    onClose();
    onAnalysisUpdated();
  };

  const handlePairChange = async (value: CryptoPair) => {
    try {
      setIsLoadingModalShown(true);
      if (sanitizerAnalysis) {
        await fetchApiRaw(
          PUT_ANALYSES,
          {
            urlParams: [sanitizerAnalysis.id],
            body: { pair: value.symbol },
          },
          accessTokens.default,
        );
      }
      await fetchApiRaw(
        PUT_ANALYSES_CMS,
        {
          urlParams: [analysis.id],
          body: { pair: value.symbol },
        },
        accessTokens.cms,
      );
      ss({ pair: value });

      await syncToMarket(
        {
          symbol: value.symbol,
          ...(existingMarket && titleForSymbol(existingMarket, value.symbol)),
        },
        'Trading pair',
      );

      setIsOpen(false);
      onClose();
    } catch (e) {
      captureException(e);
      showNotification('error', 'Failed to update trading pair');
    } finally {
      setIsLoadingModalShown(false);
    }
  };

  const handleDirectionChange = async (value: 'up' | 'down') => {
    try {
      setIsLoadingModalShown(true);
      if (sanitizerAnalysis) {
        await fetchApiRaw(
          PUT_ANALYSES,
          {
            urlParams: [sanitizerAnalysis.id],
            body: { isPredictionUp: value === 'up' },
          },
          accessTokens.default,
        );
      }
      await fetchApiRaw(
        PUT_ANALYSES_CMS,
        {
          urlParams: [analysis.id],
          body: { isPredictionUp: value === 'up' },
        },
        accessTokens.cms,
      );

      await syncToMarket(
        { isDirectionUp: value === 'up' },
        'Prediction direction',
      );

      setIsOpen(false);
      onClose();
    } catch (e) {
      captureException(e);
      showNotification('error', 'Failed to update prediction direction');
    } finally {
      setIsLoadingModalShown(false);
    }
  };

  const handleMemeChange = async (fileName: string | null) => {
    const previous = memeFileName;
    try {
      ss({ memeFileName: fileName, isMemeSaving: true });
      if (sanitizerAnalysis) {
        await fetchApiRaw(
          PUT_ANALYSES,
          {
            urlParams: [sanitizerAnalysis.id],
            body: { memeURL: fileName },
          },
          accessTokens.default,
        );
      }
      await fetchApiRaw(
        PUT_ANALYSES_CMS,
        {
          urlParams: [analysis.id],
          body: { memeURL: fileName },
        },
        accessTokens.cms,
      );
      queryClient.invalidateQueries({ queryKey: ['cms-analyses'] });
      queryClient.invalidateQueries({
        queryKey: ['cms-analysis', analysis.id],
      });
      if (sanitizerAnalysis) {
        queryClient.invalidateQueries({
          queryKey: ['play-analyses-sanitizer'],
        });
        queryClient.invalidateQueries({
          queryKey: ['sanitizer-by-tweet', analysis.tweetID],
        });
      }
      showNotification(
        'success',
        fileName ? 'Meme updated successfully' : 'Meme reset to default',
      );
    } catch (e) {
      captureException(e);
      ss({ memeFileName: previous });
      showNotification('error', 'Failed to update meme');
    } finally {
      ss({ isMemeSaving: false });
    }
  };

  const handleDelete = async (): Promise<boolean> => {
    if (!isDeleting) {
      ss({ isDeleting: true });
      return false;
    }

    if (isMarketLive) {
      showNotification(
        'error',
        'Cannot delete: the linked Score market is already live',
      );
      ss({ isDeleting: false });
      return false;
    }

    try {
      if (sanitizerAnalysis) {
        await fetchApiRaw(
          DELETE_ANALYSES,
          { urlParams: [sanitizerAnalysis.id] },
          accessTokens.default,
        );
      }
      if (existingMarket) {
        await fetchApiRaw(
          DELETE_MARKET_SCORE,
          { urlParams: [existingMarket.id] },
          accessTokens.cms,
        );
        queryClient.invalidateQueries({ queryKey: ['score-markets'] });
      }
      await fetchApiRaw(
        DELETE_ANALYSES_CMS,
        { urlParams: [analysis.id] },
        accessTokens.cms,
      );

      showNotification('success', 'Analysis deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['cms-analyses'] });
      if (sanitizerAnalysis) {
        queryClient.invalidateQueries({
          queryKey: ['play-analyses-sanitizer'],
        });
      }
      onDelete?.();
      onAnalysisUpdated();
      return true;
    } catch (e) {
      captureException(e);
      showNotification('error', 'Failed to delete analysis');
      ss({ isDeleting: false });
      onAnalysisUpdated();
      return false;
    }
  };

  const handleDeleteClick = async () => {
    const deleted = await handleDelete();
    if (deleted) {
      setIsOpen(false);
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-lg">
        {isLoadingModalShown ?
          <div className="flex flex-col items-center justify-center gap-4 p-8">
            <SpinnerCircle color="var(--textMain)" size={32} />
            <div className="text-(--textMain)">Processing...</div>
          </div>
        : <>
            <DialogHeader>
              <DialogTitle>Edit Analysis</DialogTitle>
            </DialogHeader>

            <div className="text-sm text-(--textSecondary) mb-6">
              <div>
                <strong>Author:</strong> {analysis.authorName} (@
                {analysis.authorUsername}){' id:'}
                <span className="text-xs">{analysis.tweetID}</span>
              </div>
              <div className="mt-1 line-clamp-2">
                <strong>Tweet:</strong> {analysis.tweetText}
              </div>
            </div>

            <div className="space-y-6">
              <div>
                <Label className="text-sm font-medium mb-2 block">
                  Trading Pair
                </Label>
                <Popover open={comboboxOpen} onOpenChange={setComboboxOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={comboboxOpen}
                      className="w-full justify-between"
                    >
                      <div className="flex items-center gap-2">
                        {pair?.logoUrl && (
                          <img
                            src={pair.logoUrl}
                            alt={pair.symbol}
                            className="w-5 h-5 rounded"
                          />
                        )}
                        {pair?.symbol || 'Select trading pair...'}
                      </div>
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[--radix-popover-trigger-width] p-0">
                    <Command shouldFilter={false}>
                      <CommandInput
                        placeholder="Search for trading pair..."
                        value={pairSearchInput}
                        onValueChange={setPairSearchInput}
                      />
                      <CommandList>
                        <CommandEmpty>No pair found.</CommandEmpty>
                        <CommandGroup>
                          {filteredPairs.map((pairItem) => (
                            <CommandItem
                              key={pairItem.symbol}
                              value={pairItem.symbol}
                              onSelect={() => {
                                setComboboxOpen(false);
                                if (!pair || pair.symbol !== pairItem.symbol) {
                                  handlePairChange(pairItem as CryptoPair);
                                }
                              }}
                            >
                              <Check
                                className={cn(
                                  'mr-2 h-4 w-4',
                                  pair?.symbol === pairItem.symbol ?
                                    'opacity-100'
                                  : 'opacity-0',
                                )}
                              />
                              <img
                                src={pairItem.icon || ''}
                                alt={pairItem.label}
                                className="w-5 h-5 rounded mr-2"
                              />
                              {pairItem.label}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              <div>
                <Label className="text-sm font-medium mb-2 block">
                  Prediction Direction
                </Label>
                <RadioGroup
                  value={analysis.isPredictionUp ? 'up' : 'down'}
                  onValueChange={(value) =>
                    handleDirectionChange(value as 'up' | 'down')
                  }
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="up" id="up" />
                    <Label htmlFor="up">Up</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="down" id="down" />
                    <Label htmlFor="down">Down</Label>
                  </div>
                </RadioGroup>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium">Meme</Label>
                  {hasPerformance && (
                    <span className="text-xs text-(--textSecondary)">
                      Performance{' '}
                      <span
                        className={
                          performance < 0 ? 'text-red-500' : 'text-green-600'
                        }
                      >
                        {(performance * 100).toFixed(2)}%
                      </span>
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-2">
                  {[GOOD_MEMES, BAD_MEMES].map((row, rowIdx) => {
                    const isActive = isMemeRowActive(rowIdx === 0);
                    return (
                      <div key={rowIdx} className="grid grid-cols-7 gap-2">
                        {row.map((name) => {
                          const isSelected = memeFileName === name;
                          return (
                            <button
                              key={name}
                              type="button"
                              onClick={() => {
                                if (!isSelected) handleMemeChange(name);
                              }}
                              disabled={!isActive || isMemeSaving}
                              title={
                                isActive ? name : (
                                  'Does not match the prediction outcome'
                                )
                              }
                              className={cn(
                                'rounded border-2 overflow-hidden transition-all',
                                isSelected ?
                                  'border-blue-500 opacity-100'
                                : 'border-transparent opacity-40',
                                isActive && !isSelected && 'hover:opacity-70',
                                !isActive && 'opacity-15 cursor-not-allowed',
                              )}
                            >
                              <img
                                src={MEME_URLS[name]}
                                alt={name}
                                className="w-full aspect-square object-cover"
                              />
                            </button>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
                <div className="mt-2 flex items-center gap-3 text-xs text-(--textSecondary)">
                  {isMemeSaving ?
                    <span className="flex items-center gap-2">
                      <SpinnerCircle color="var(--textSecondary)" size={12} />
                      Saving...
                    </span>
                  : memeFileName ?
                    <button
                      type="button"
                      onClick={() => handleMemeChange(null)}
                      className="underline hover:text-(--textMain)"
                    >
                      Reset to default
                    </button>
                  : <span>Default meme (picked by performance)</span>}
                </div>
              </div>

              <div className="pt-4 border-t border-text-tertiary">
                <h3 className="text-lg font-semibold text-red-500 mb-3">
                  Danger Zone
                </h3>

                {isMarketLive && (
                  <div className="mb-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <p className="text-sm text-yellow-600 font-medium">
                      Deletion blocked — the linked Score market is already live
                    </p>
                  </div>
                )}

                {existingMarket && !isMarketLive && (
                  <div className="mb-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium">
                      Deleting this analysis will also delete the linked Score
                      market
                    </p>
                  </div>
                )}

                {sanitizerAnalysis && (
                  <div className="mb-3 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-sm text-green-700 font-medium">
                      Deleting this analysis will also remove it from the
                      Sanitizer
                    </p>
                  </div>
                )}

                <div className="flex gap-4 bg-red-500/10 p-4 rounded-lg">
                  <Button
                    variant="destructive"
                    onClick={handleDeleteClick}
                    disabled={isMarketLive}
                  >
                    {isDeleting ?
                      'Click again to confirm DELETE'
                    : 'DELETE ANALYSIS'}
                  </Button>
                  {isDeleting && (
                    <Button
                      variant="outline"
                      onClick={() => ss({ isDeleting: false })}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </div>

            <DialogFooter className="mt-6 pt-4 border-t border-text-tertiary">
              <Button variant="outline" onClick={handleClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        }
      </DialogContent>
    </Dialog>
  );
}
